/**
 * ARInspectorClockTab - Clock in/out for inspectors doing on-site AR reviews.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Alert, CircularProgress,
} from "@mui/material";
import LoginIcon   from "@mui/icons-material/Login";
import LogoutIcon  from "@mui/icons-material/Logout";
import TimerIcon   from "@mui/icons-material/Timer";
import api from "../../utils/api";

const fmtDT  = (d) => d ? new Date(d).toLocaleString() : "N/A";
const fmtDur = (min) => {
  if (min == null) return "In Progress";
  const h = Math.floor(min / 60), m = min % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// Live timer showing elapsed time since clock-in
const LiveTimer = ({ startTime }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const base = startTime ? Math.floor((Date.now() - new Date(startTime).getTime()) / 1000) : 0;
    setElapsed(base);
    const t = setInterval(() => setElapsed(p => p + 1), 1000);
    return () => clearInterval(t);
  }, [startTime]);
  const h = Math.floor(elapsed / 3600), m = Math.floor((elapsed % 3600) / 60), s = elapsed % 60;
  return <span>{h > 0 ? `${h}h ` : ""}{m}m {s}s</span>;
};

const ARInspectorClockTab = ({ requestId, currentUserId }) => {
  const [sessions, setSessions] = useState([]);
  const [active,   setActive]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [acting,   setActing]   = useState(false);
  const [snack,    setSnack]    = useState({ msg:"", sev:"success" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Re-use WO clock endpoints adapted for AR - fallback to time-logs
      const [sessRes, activeRes] = await Promise.allSettled([
        api.get(`/ar-inspector-sessions/${requestId}`).catch(() => ({ data:[] })),
        api.get(`/ar-inspector-sessions/${requestId}/active`).catch(() => ({ data: null })),
      ]);
      if (sessRes.status === "fulfilled") setSessions(sessRes.value.data || []);
      if (activeRes.status === "fulfilled") setActive(activeRes.value.data?.session || null);
    } catch {} finally { setLoading(false); }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const handleClockIn = async () => {
    setActing(true);
    try {
      await api.post(`/ar-inspector-sessions/${requestId}/clock-in`, { user_id: currentUserId });
      setSnack({ msg:"Clocked in.", sev:"success" }); load();
    } catch (e) { setSnack({ msg: e.response?.data?.error || "Clock-in failed.", sev:"error" }); }
    finally { setActing(false); }
  };

  const handleClockOut = async () => {
    setActing(true);
    try {
      await api.post(`/ar-inspector-sessions/${requestId}/clock-out`, { user_id: currentUserId });
      setSnack({ msg:"Clocked out.", sev:"success" }); load();
    } catch (e) { setSnack({ msg: e.response?.data?.error || "Clock-out failed.", sev:"error" }); }
    finally { setActing(false); }
  };

  const totalMin = sessions.reduce((a, s) => a + (s.duration_minutes || 0), 0);

  return (
    <Box>
      {snack.msg && <Alert severity={snack.sev} onClose={() => setSnack({ msg:"", sev:"success" })} sx={{ mb:2, borderRadius:2 }}>{snack.msg}</Alert>}

      {/* Clock card */}
      <Paper elevation={0} sx={{ borderRadius:"14px", border:"2px solid rgba(47,99,173,0.15)", p:2.5, mb:2.5 }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mb:2 }}>
          <TimerIcon sx={{ color:"#2f63ad", fontSize:28 }} />
          <Box sx={{ flex:1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142" }}>
              {active ? "Currently On Site" : "Inspector Clock In/Out"}
            </Typography>
            {active && (
              <Typography variant="caption" sx={{ color:"#6b778c" }}>
                On site since {fmtDT(active.ClockIn)} &mdash; <LiveTimer startTime={active.ClockIn} />
              </Typography>
            )}
          </Box>
          {!active ? (
            <Button variant="contained" startIcon={acting ? <CircularProgress size={14} color="inherit" /> : <LoginIcon />}
              onClick={handleClockIn} disabled={acting}
              sx={{ borderRadius:"10px", fontWeight:700, textTransform:"none", boxShadow:"none",
                bgcolor:"#16a34a", "&:hover":{ bgcolor:"#15803d", boxShadow:"none" } }}>
              Clock In
            </Button>
          ) : (
            <Button variant="contained" startIcon={acting ? <CircularProgress size={14} color="inherit" /> : <LogoutIcon />}
              onClick={handleClockOut} disabled={acting}
              sx={{ borderRadius:"10px", fontWeight:700, textTransform:"none", boxShadow:"none",
                bgcolor:"#dc2626", "&:hover":{ bgcolor:"#b91c1c", boxShadow:"none" } }}>
              Clock Out
            </Button>
          )}
        </Box>

        <Box sx={{ display:"flex", gap:3 }}>
          {[
            { label:"Total Inspections", value:sessions.length, color:"#223142" },
            { label:"Total Time",        value:`${Math.floor(totalMin/60)}h ${totalMin%60}m`, color:"#2f63ad" },
          ].map(({ label, value, color }) => (
            <Box key={label} sx={{ textAlign:"center", minWidth:80 }}>
              <Typography variant="h5" sx={{ fontWeight:800, color }}>{value}</Typography>
              <Typography variant="caption" sx={{ color:"#6b778c" }}>{label}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Session log */}
      <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
        <Box sx={{ px:2, py:1.5, bgcolor:"rgba(47,99,173,0.03)", borderBottom:"1px solid rgba(47,99,173,0.08)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142" }}>Inspection Session Log</Typography>
        </Box>
        {loading ? (
          <Box sx={{ py:4, textAlign:"center" }}><CircularProgress size={24} /></Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor:"rgba(47,99,173,0.03)" }}>
              <TableRow>
                {["Inspector","Clock In","Clock Out","Duration","Notes"].map(h => (
                  <TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((s, i) => (
                <TableRow key={s.id || i} hover>
                  <TableCell sx={{ fontWeight:600, color:"#223142", fontSize:12 }}>{s.user_name || "Inspector"}</TableCell>
                  <TableCell sx={{ fontSize:12, color:"#6b778c" }}>{fmtDT(s.ClockIn || s.start_time)}</TableCell>
                  <TableCell sx={{ fontSize:12, color:"#6b778c" }}>
                    {s.ClockOut || s.end_time ? fmtDT(s.ClockOut || s.end_time) :
                      <Chip size="small" label="Active" sx={{ bgcolor:"rgba(22,163,74,0.1)", color:"#16a34a", fontWeight:700, fontSize:10 }} />}
                  </TableCell>
                  <TableCell sx={{ fontSize:12, fontWeight:600, color:"#374151" }}>{fmtDur(s.duration_minutes)}</TableCell>
                  <TableCell sx={{ fontSize:11, color:"#6b778c" }}>{s.notes || "-"}</TableCell>
                </TableRow>
              ))}
              {!sessions.length && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign:"center", py:4, color:"#6b778c" }}>
                    No inspection sessions yet. Clock in when you arrive for an on-site review.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

export default ARInspectorClockTab;
